import type { AxiosInstance } from 'axios';
import type * as QueryApi from './interfaces/queries';
import { transformChatInstance, transformChatQuery } from './transformers';
import { download } from './utils';

export class QueriesClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  updateQueryRating = ({ chatInstanceId, queryIdentifier, ...data }: QueryApi.UpdateQueryRating.Request) => {
    return this.authenticatedClient.patch<QueryApi.UpdateQueryRating.Response>(`/chat/${chatInstanceId}/queries/${queryIdentifier}/rating`, data)
      .then(res => ({
        query: transformChatQuery(res.data.query),
      }));
  };

  updateQueryFeedback = ({ chatInstanceId, queryIdentifier, ...data }: QueryApi.UpdateQueryFeedback.Request) => {
    return this.authenticatedClient.patch<QueryApi.UpdateQueryFeedback.Response>(`/chat/${chatInstanceId}/queries/${queryIdentifier}/feedback`, data)
      .then(res => ({
        query: transformChatQuery(res.data.query),
      }));
  };

  getQueryFeedback = ({ chatInstanceId, queryIdentifier }: QueryApi.GetQueryFeedback.Request) => {
    return this.authenticatedClient.get<QueryApi.GetQueryFeedback.Response>(`/chat/${chatInstanceId}/queries/${queryIdentifier}/feedback`)
      .then(res => ({
        feedback: res.data.feedback,
      }));
  };

  getQuery = ({ chatInstanceId, queryIdentifier }: QueryApi.GetQuery.Request) => {
    return this.authenticatedClient.get<QueryApi.GetQuery.Response>(`/chat/${chatInstanceId}/queries/${queryIdentifier}`)
      .then(res => ({
        chatInstance: transformChatInstance(res.data.chatInstance),
        query: transformChatQuery(res.data.query),
      }));
  };

  downloadQueryTable = async ({ chatInstanceId, queryIdentifier }: QueryApi.DownloadQueryTable.Request) => {
    const res = await this.authenticatedClient.get<Blob>(`/chat/${chatInstanceId}/queries/${queryIdentifier}/download-table-data`, {
      responseType: 'blob',
    });

    const contentDisposition = res.headers['content-disposition'] as string;

    const fileName = new RegExp(/filename="(.+)"/).exec(contentDisposition)?.[1];
    return {
      blob: res.data,
      fileName,
    };
  };

  cancelQueryHcpSelection = ({ chatInstanceId, queryIdentifier }: QueryApi.CancelQueryHcpSelection.Request) => {
    return this.authenticatedClient.patch<QueryApi.CancelQueryHcpSelection.Response>(`/chat/${chatInstanceId}/queries/${queryIdentifier}/cancel-hcp-selection`);
  };

  downloadHcpResults = async ({ brandIdentifier, chatInstanceId, queryIdentifier }: QueryApi.DownloadHcpResults.Request) => {
    return download(this.authenticatedClient, `/chat/${chatInstanceId}/queries/${queryIdentifier}/download-hcp-results?brandIdentifier=${brandIdentifier}`);
  };

  getQueryResponseText = ({ chatInstanceId, queryId, tts }: QueryApi.GetQueryResponseText.Request) => {
    return this.authenticatedClient.get<QueryApi.GetQueryResponseText.Response>(`/chat/${chatInstanceId}/queries/${queryId}/response-text?tts=${tts}`)
      .then(res => res.data.value);
  };

  downloadQueryResponse = ({ chatId, queryIdentifier }: QueryApi.DownloadQueryResponse.Request) => {
    return download(this.authenticatedClient, `/chat/${chatId}/queries/${queryIdentifier}/download`);
  };

  cancelQuery = ({ chatId, queryIdentifier }: QueryApi.CancelQuery.Request) => {
    return this.authenticatedClient.patch(`/chat/${chatId}/queries/${queryIdentifier}/cancel`);
  };

  updateQuerySmartRouting = ({ chatInstanceId, queryIdentifier, smartRoutingState }: QueryApi.UpdateQuerySmartRouting.Request) => {
    return this.authenticatedClient.patch<QueryApi.UpdateQuerySmartRouting.Response>(`/chat/${chatInstanceId}/queries/${queryIdentifier}/smart-routing`, {
      smartRoutingState,
    })
      .then(res => ({
        smartRoutingState: res.data.smartRoutingState,
      }));
  };
}

export default QueriesClient;